/**
 * §13.1.5 Pause. Centre panel over a 60%-dimmed live frame: Resume, Settings,
 * Restart Run (inline hold-to-confirm), Abandon to Title. Right column carries the
 * build recap — weapons and passives with levels, per-weapon damage meters, seed.
 *
 * On a 390 px viewport the recap stacks under the buttons instead of beside them.
 */

import { weaponById, passiveById } from '../../core/content';
import { ichorBalance } from '../../sim/gacha';
import { canBuySupply, SUPPLY_ITEMS, supplyCost } from '../../sim/supply';
import { button, delta, div, holdConfirm, meter, meterFill, panel, panelBody, setMeter, setText } from '../dom';
import { EventLogView, type EventLogStore } from '../event-log';
import { formatInt, formatTime, humanizeId } from '../format';
import { HullPreview } from '../hull-preview';
import { StatusLine, type StatusFields } from '../status-line';
import { shipFormOf } from '../../sim/build';
import type { World } from '../../sim/world';
import { Screen, type ActionSink } from './base';

export class PauseScreen extends Screen {
  private recap: HTMLElement;
  private timeText: HTMLElement;
  /**
   * The hull at inspect size. AMD-50 draws weapon marks into the silhouette, but at the
   * run's 34 px they read as texture, not identity ("glyphs when you stop"). This is the
   * stopped surface: the same `markedOutline` ring at ~4× run size, where the 12-glyph
   * set separates (measured ≥78 px).
   */
  private hull = new HullPreview('pause-hull');
  /** AMD-13 — the run so far, in the dwarf.land readout register. */
  readonly log = new EventLogView(10);
  readonly status = new StatusLine();
  private baseStops: HTMLElement[] = [];
  private supply: HTMLElement;

  constructor(emit: ActionSink) {
    super('pause', emit, 'is-modal');

    const p = panel('pause-panel');
    const body = panelBody(p);
    body.appendChild(div('screen-title', 'PAUSED'));

    const clock = div('pause-clock');
    this.timeText = div('pause-time', '00:00');
    clock.appendChild(this.timeText);
    body.appendChild(clock);

    const menu = div('pause-menu');
    const resume = button('Resume', 'primary', 'play');
    resume.addEventListener('click', () => this.emit({ type: 'resume' }));

    const settings = button('Settings', '', 'cog');
    settings.addEventListener('click', () => this.emit({ type: 'goto', screen: 'settings' }));

    const restart = button('Restart Run', '', 'refresh');
    holdConfirm(restart, 'Restart Run', 'Hold to confirm', () => this.emit({ type: 'restart_run' }));

    const abandon = button('Abandon to Title', 'danger', 'logout');
    holdConfirm(abandon, 'Abandon to Title', 'Hold to confirm', () => this.emit({ type: 'abandon_run' }));

    menu.appendChild(resume);
    menu.appendChild(settings);
    menu.appendChild(restart);
    menu.appendChild(abandon);
    body.appendChild(menu);

    /*
     * The inspect view is appended AFTER the menu now, rather than before it and dragged
     * back by `order: -1` on the menu. Cycle 009 grafted this arrangement — "pause is usually
     * pressed to stop and resume, so the way out should not sit behind a picture of your
     * ship" — and expressed it with flex `order`. Multi-column layout lays out in DOM order
     * and ignores `order` entirely, so the sequence has to be real for the panel to be able
     * to use the width it has. Same result on one column, and now it survives two.
     */
    const inspect = div('pause-inspect');
    inspect.appendChild(this.hull.canvas);
    body.appendChild(inspect);

    this.recap = div('build-recap');
    body.appendChild(this.recap);

    // AMD-52 — the Supply Drop. Rebuilt on every refresh alongside the recap.
    this.supply = div('supply-drop');
    body.appendChild(this.supply);

    body.appendChild(this.log.root);
    body.appendChild(this.status.root);

    this.root.appendChild(p);
    this.baseStops = [resume, settings, restart, abandon];
    this.stops = this.baseStops.concat(this.log.focusStops());
  }

  /** AMD-13 — log rows and the status line, refreshed the moment the panel opens. */
  presentLog(store: EventLogStore, fields: StatusFields): void {
    this.log.render(store);
    this.status.set(fields);
  }

  /** Rebuilt only on show — the pause screen is never in the per-frame path. */
  refresh(w: World): void {
    const p = w.player;
    setText(this.timeText, formatTime(w.tick / 60));
    // `set` copies the ring immediately and keys repaints on `form.signature`, so the
    // live `ShipForm` is safe to hand over and re-shows are free.
    this.hull.set(w.hullSeed, shipFormOf(w), { hot: true });

    while (this.recap.firstChild) this.recap.removeChild(this.recap.firstChild);

    const weaponsHead = div('recap-head', 'Weapons');
    this.recap.appendChild(weaponsHead);

    let maxDamage = 1;
    for (const key of Object.keys(w.counters.damage_by_weapon)) {
      const v = w.counters.damage_by_weapon[key];
      if (v > maxDamage) maxDamage = v;
    }

    for (let i = 0; i < p.weapon_count; i++) {
      const slot = p.weapons[i];
      if (!slot.def_id) continue;
      const def = weaponById.get(slot.def_id);
      const row = div('recap-row');
      const name = div('recap-name', def ? def.display_name : humanizeId(slot.def_id));
      if (slot.evolved) name.classList.add('is-evolved');
      row.appendChild(name);
      row.appendChild(delta('Lv ' + slot.level, slot.evolved ? 'surge' : 'dim'));
      const dmg = w.counters.damage_by_weapon[slot.def_id] ?? 0;
      const m = meter('recap-meter');
      setMeter(meterFill(m), dmg / maxDamage);
      row.appendChild(m);
      row.appendChild(div('recap-damage', formatInt(dmg)));
      this.recap.appendChild(row);
    }

    this.recap.appendChild(div('recap-head', 'Passives'));
    for (let i = 0; i < p.passive_count; i++) {
      const ps = p.passives[i];
      if (!ps.def_id) continue;
      const def = passiveById.get(ps.def_id);
      const row = div('recap-row');
      row.appendChild(div('recap-name', def ? String(def.display_name) : humanizeId(ps.def_id)));
      row.appendChild(delta('Rank ' + ps.rank, 'dim'));
      this.recap.appendChild(row);
    }

    // AMD-52 — the Supply Drop: certainty, priced against the Graft. A disabled row still
    // renders, because "you cannot afford this" is the tension being displayed.
    while (this.supply.firstChild) this.supply.removeChild(this.supply.firstChild);
    const supplyStops: HTMLElement[] = [];
    this.supply.appendChild(div('recap-head', 'Supply Drop — ' + formatInt(ichorBalance(w)) + ' ichor'));
    for (const item of SUPPLY_ITEMS) {
      const row = div('supply-row');
      const buy = button(item.name, '', 'download');
      buy.disabled = !canBuySupply(w, item.id);
      buy.addEventListener('click', () => this.emit({ type: 'supply_buy', item_id: item.id }));
      row.appendChild(buy);
      row.appendChild(div('supply-effect', item.effect));
      row.appendChild(delta(formatInt(supplyCost(w, item)) + ' ichor', buy.disabled ? 'dim' : 'surge'));
      this.supply.appendChild(row);
      if (!buy.disabled) supplyStops.push(buy);
    }
    this.stops = this.baseStops.concat(supplyStops, this.log.focusStops());
  }

  override onBack(): boolean {
    this.emit({ type: 'resume' });
    return true;
  }
}
